import React from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Paper
} from '@mui/material';

import { styled } from '@mui/material/styles';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import exampleFile from '../helpers/example_input.xlsx';
// Styled components
const CategoryContainer = styled(Box)(({ theme, borderColor, bgcolor }) => ({
    padding: '26px',
    border: `1.5px solid ${borderColor}`,
    marginBottom: '28px',
    borderRadius: '18px',
    backgroundColor: bgcolor || 'transparent',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 10px 30px ${bgcolor}80`
    }
}));

const CategoryHeader = styled(Box)(({ theme, color }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '22px',
    paddingBottom: '18px',
    borderBottom: `1.5px solid ${color}30`
}));

const UploadCard = styled(Paper)(({ theme, borderColor, bgcolor }) => ({
    background: bgcolor || '#F8FAFC',
    border: `2px dashed ${borderColor}`,
    borderRadius: '14px',
    padding: '20px',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    minHeight: '180px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        borderColor: borderColor,
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 24px ${bgcolor}`,
    }
}));

const FilePreviewCard = styled(Paper)(({ theme }) => ({
    background: 'white',
    border: '1px solid #E0E0E0',
    borderRadius: '8px',
    marginTop: '12px'
}));

const QuickSimulationSetup = ({
    settingsValues,
    handleChange,
    generateSimulationId,
    uploadedFiles,
    handleFileUpload,
    fileInputRefs,
    formData,
    setFormData
}) => {
    // Check if data file is required based on "Based on input file" selections
    const isDataRequired = Object.entries(settingsValues).some(([settingKey, settingValue]) => {
        if (settingValue.toString() === "Based on input file") {
            return true;
        }
        return false;
    });
    
    // Handler for downloading the sample Excel file
    const handleDownloadSample = () => {
        const link = document.createElement('a');
        link.href = exampleFile;
        link.download = 'example_input.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    
    // Don't show the upload section if no file uploads are required
    if (!isDataRequired) {
        return null;
    }

    return (
        <CategoryContainer
            borderColor={"#757575"}
            elevation={1}
        >
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadSample}
                    sx={{
                        color: '#757575',
                        borderColor: '#757575',
                        '&:hover': {
                            borderColor: '#5e5e5e',
                            backgroundColor: 'rgba(117, 117, 117, 0.04)'
                        }
                    }}
                >
                    Download Sample File
                </Button>
            </Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={12}>
                            <UploadCard
                                borderColor={"#757575"}
                                bgcolor="#75757530"
                                onClick={() => fileInputRefs.current.data?.click()}
                            >
                                <CloudUploadIcon sx={{ fontSize: '32px', color:  '#757575', mb: 1 }} />
                                <Typography variant="h6" sx={{ color: '#757575', fontWeight: 600, mb: 1 }}>
                                    Data Upload
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
                                    Upload data (XLSX)
                                </Typography>
                                <input
                                    ref={el => fileInputRefs.current.data = el}
                                    type="file"
                                    style={{ display: 'none' }}
                                    accept=".xlsx"
                                    onChange={(e) => handleFileUpload('data', e.target.files[0])}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<CloudUploadIcon />}
                                    sx={{
                                        backgroundColor: '#757575',
                                        '&:hover': {
                                            backgroundColor: '#5e5e5e',
                                            opacity: 0.9
                                        }
                                    }}
                                >
                                    Choose File
                                </Button>
                                {uploadedFiles.data && (
                                    <FilePreviewCard>
                                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontSize: '20px' }}>📄</Typography>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#757575' }}>
                                                        {uploadedFiles.data.name}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {(uploadedFiles.data.size / 1024).toFixed(1)} KB
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Button
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleFileUpload('data', null);
                                                }}
                                                startIcon={<DeleteIcon />}
                                                sx={{ color: 'error.main', minWidth: 'auto' }}
                                            >
                                                Remove
                                            </Button>
                                        </Box>
                                    </FilePreviewCard>
                                )}
                            </UploadCard>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </CategoryContainer>
    );
};

export default QuickSimulationSetup;
